import { useEffect, useState } from 'react';
import { Trip } from '../../schema';
import { z } from 'zod';
import { usePostInteractionsByTripByTripIdAndLinkSecretViewsMutation } from '../../store/backendApi';

const TripVisitStatsScheme = z.object({
  lastViewCountedAt: z.number().optional(),
  lastVisitAt: z.number().optional(),
});

type TripVisitStats = z.infer<typeof TripVisitStatsScheme>;
export type LastVisitedTimestamp = number;

const TWENTY_FOUR_HOURS_IN_SECONDS = 24 * 60 * 60;
const VISIT_EXPIRES_AFTER = TWENTY_FOUR_HOURS_IN_SECONDS;
const now = () => Math.floor(Date.now() / 1000);
const twentyThreeAndHalfHoursAgo = () => now() - (23 * 60 * 60 + 30 * 60);
const getKey = (tripId: string) => `tvs_${tripId}`;

export const useTripVisitStats = (tripData: { trip: Trip } | null, linkSecret: string | null) => {
  const [value, setValue] = useState<LastVisitedTimestamp | undefined>();

  const [postAddViewCount, { isSuccess }] =
    usePostInteractionsByTripByTripIdAndLinkSecretViewsMutation();

  useEffect(() => {
    if (!tripData || !tripData.trip) {
      return;
    }
    let stats: TripVisitStats = {};
    const item = localStorage.getItem(getKey(tripData.trip.id));
    if (item) {
      const jsonParsed = JSON.parse(item);
      stats = TripVisitStatsScheme.parse(jsonParsed);
      if (stats.lastViewCountedAt && stats.lastViewCountedAt - now() > VISIT_EXPIRES_AFTER) {
        stats.lastViewCountedAt = undefined;
      }
    }
    setValue(stats.lastVisitAt);

    stats.lastVisitAt = now();
    if (!stats.lastViewCountedAt) {
      const addViewCount = async () => {
        await postAddViewCount({
          tripId: tripData.trip.id,
          linkSecret: linkSecret ?? 'none',
        });
        if (isSuccess) {
          stats.lastViewCountedAt = now();
        } else {
          stats.lastViewCountedAt = twentyThreeAndHalfHoursAgo();
        }
        const updatedItem = JSON.stringify(stats);
        localStorage.setItem(getKey(tripData.trip.id), updatedItem);
      };
      addViewCount();
    }
  }, [tripData]);

  return value;
};
